import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { getKey } from "@redotech/react-util/key";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import {
  AxisKind,
  defaultNewReturnsRateTable,
  isReturnsWeightByZoneRateTable,
  RateTable,
  RateTableType,
  RateTableValueMode,
  ReturnsWeightByZoneRateTable,
  WeightUnit,
} from "@redotech/redo-fulfillment-rate-table-common/rate-table";
import {
  RedoShopifyCountryCode,
  RedoShopifyCountryNames,
} from "@redotech/redo-inventory-common/fulfillment-country-code";
import { MerchantCarrierReturnLabelMode } from "@redotech/redo-return-settings-common/merchant-carrier-return-label-schema";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import CopyIcon from "@redotech/redo-web/icon-old/copy.svg";
import PlusIcon from "@redotech/redo-web/icon-old/plus.svg";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";
import { memo, useEffect, useState } from "react";
import { AuthContext } from "../../../../app/auth";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import { LABEL_RATE_AUTHORIZED_EMAILS } from "../../../../returns/constants";
import { TeamContext } from "../../../team";
import { LabelRateTableForm } from "../../common/rate-tables/rate-table";
import {
  rateCardForm,
  RateCardValue,
  rateTableToTableData,
  tableDataToMinMaxWeights,
  tableDataToRateData,
  tableDataToZones,
} from "../../common/rate-tables/rate-table-utils";

export const returnLabelsForm = groupInput({
  disableMerchantPaidUpcharge: input<boolean>(),
  rateSourceSelection: groupInput({ enabled: input<boolean>() }),
  merchantCarrierReturnLabels: groupInput({
    mode: input<MerchantCarrierReturnLabelMode>(),
    whitelistedCountries: listInput(
      () => input<RedoShopifyCountryCode>(),
      () => RedoShopifyCountryCode.Us,
      (i) => i,
    ),
  }),
});

export const returnLabelsDefault = {
  disableMerchantPaidUpcharge: false,
  rateSourceSelection: { enabled: false },
  merchantCarrierReturnLabels: {
    mode: MerchantCarrierReturnLabelMode.ONLY_REDO_CARRIERS,
    whitelistedCountries: [],
  },
};

export const LabelRatesCard = memo(function LabelRatesCard({
  input,
}: {
  input: InputSchema.Input<typeof returnLabelsForm>;
}) {
  const auth = useRequiredContext(AuthContext);
  const canSee = LABEL_RATE_AUTHORIZED_EMAILS.includes(auth.email);
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoAdminRpcClientContext);
  // This is to load the rate table in the backend and transform it into the schema needed for the form
  const [rateTableLoad, triggerRateTableLoad] = useTriggerLoad(async () => {
    const tables = await client.getTeamRateTables({ teamId: team._id });
    const filtered: ReturnsWeightByZoneRateTable | undefined =
      tables.rateTables.find((table) => isReturnsWeightByZoneRateTable(table));
    if (!filtered) {
      return undefined;
    }
    return {
      defaultValue: filtered.defaultValue,
      defaultValuePercentage:
        filtered.defaultValuePercentage === null
          ? undefined
          : filtered.defaultValuePercentage,
      useDefaultValuePercentage:
        filtered.useDefaultValuePercentage === null
          ? undefined
          : filtered.useDefaultValuePercentage,
      data: rateTableToTableData(filtered),
    };
  });
  useEffect(() => {
    triggerRateTableLoad();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, client]);

  if (!canSee) {
    return null;
  }

  return (
    <LabelRatesCardInternal
      input={input}
      key={getKey(rateTableLoad.value)}
      rateTableInDb={rateTableLoad.value}
      triggerReload={triggerRateTableLoad}
    />
  );
});

const LabelRatesCardInternal = memo(function LabelRatesCardInternal({
  input,
  rateTableInDb,
  triggerReload,
}: {
  input: InputSchema.Input<typeof returnLabelsForm>;
  rateTableInDb: RateCardValue | undefined;
  triggerReload: () => void;
}) {
  const [savePending, setSavePending] = useState(false);

  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoAdminRpcClientContext);

  const initialRateTable = rateTableInDb ?? {
    ...defaultNewReturnsRateTable,
    data: rateTableToTableData(defaultNewReturnsRateTable),
  };

  const rateTableDataInput = useInput(rateCardForm, {
    defaultValue: initialRateTable.defaultValue,
    defaultValuePercentage:
      initialRateTable.defaultValuePercentage === null
        ? undefined
        : initialRateTable.defaultValuePercentage,
    useDefaultValuePercentage:
      initialRateTable.useDefaultValuePercentage === null
        ? undefined
        : initialRateTable.useDefaultValuePercentage,
    data: initialRateTable.data,
  });

  const addBottomRow = () => {
    const bottomRow =
      rateTableDataInput.value.data.at(-1)?.map((_) => "0") ?? [];
    rateTableDataInput.inputs.data.setValue([
      ...rateTableDataInput.value.data,
      bottomRow,
    ]);
  };

  const addRightColumn = () => {
    const newColumn =
      rateTableDataInput.value.data.map((row) => row.at(-1)).map((_) => "0") ??
      [];
    const newCharges = rateTableDataInput.value.data.map((row, index) => [
      ...row,
      newColumn.at(index) ?? "0",
    ]);
    rateTableDataInput.inputs.data.setValue(newCharges);
  };

  const deleteRow = (index: number) => {
    rateTableDataInput.inputs.data.setValue(
      rateTableDataInput.value.data.filter((_, i) => i !== index),
    );
  };

  const deleteColumn = (columnIndex: number) => {
    rateTableDataInput.inputs.data.setValue(
      rateTableDataInput.value.data.map((row) =>
        row.filter((_, i) => i !== columnIndex),
      ),
    );
  };

  const copyTable = () => {
    const data = rateTableDataInput.value.data.map((row) => [...row]);
    // Make sure we copy a blank cell for the upper left cell, it's useless
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    data[0][0] = "";
    const excelData = data.map((row) => row.join("\t")).join("\n");
    void navigator.clipboard.writeText(excelData);
  };

  const save = async () => {
    setSavePending(true);
    const updatedRateTable: RateTable = {
      rateTableType: RateTableType.RETURNS_WEIGHT_BY_ZONE,
      valueMode: RateTableValueMode.Spread,
      provider: RateProvider.EASYPOST,
      data: tableDataToRateData(rateTableDataInput.value.data),
      xAxis: {
        values: tableDataToZones(rateTableDataInput.value.data),
        kind: AxisKind.ZONE,
      },
      yAxis: {
        values: tableDataToMinMaxWeights(rateTableDataInput.value.data),
        kind: AxisKind.WEIGHT,
        units: WeightUnit.LB,
      },
      defaultValue: rateTableDataInput.value.defaultValue,
    };

    try {
      await client.saveTeamRateTable({
        rateTable: updatedRateTable,
        teamId: team._id,
      });
    } catch (error) {
      toast("Failed to save rate table", { variant: "error" });
      console.error(error);
    } finally {
      setSavePending(false);
      triggerReload();
    }
  };

  const [countryInputValue, setCountryInputValue] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [countryAnchorRef, setCountryAnchorRef] =
    useState<HTMLDivElement | null>(null);

  const { merchantCarrierReturnLabels, rateSourceSelection } = input.inputs;

  return (
    <Card title="Merchant return label settings">
      <Flex dir="column" gap="md">
        <RedoToggle
          label="Disable merchant paid label up-charge"
          setValue={input.inputs.disableMerchantPaidUpcharge.setValue}
          size="sm"
          value={input.inputs.disableMerchantPaidUpcharge.value}
        />

        <Flex dir="column" gap="xl">
          <RedoToggle
            description="Allow merchants to configure which carrier accounts (both Redo and merchant), and dropoffs are used for return rate shopping in the locations flow. This setting may affect margin and reliability of getting return labels."
            label="Rate source selection"
            setValue={rateSourceSelection.inputs.enabled.setValue}
            size="sm"
            value={rateSourceSelection.value.enabled}
          />

          <RedoToggle
            description="If enabled, merchants can use their own carriers for return labels based on the settings below"
            label="Use merchant carriers for return labels"
            setValue={(enabled) => {
              if (enabled) {
                merchantCarrierReturnLabels.inputs.mode.setValue(
                  MerchantCarrierReturnLabelMode.ONLY_SOME_COUNTRIES,
                );
              } else {
                merchantCarrierReturnLabels.inputs.mode.setValue(
                  MerchantCarrierReturnLabelMode.ONLY_REDO_CARRIERS,
                );
              }
            }}
            size="sm"
            value={
              merchantCarrierReturnLabels.value.mode !==
              MerchantCarrierReturnLabelMode.ONLY_REDO_CARRIERS
            }
          />

          {merchantCarrierReturnLabels.value.mode !==
            MerchantCarrierReturnLabelMode.ONLY_REDO_CARRIERS && (
            <>
              <RedoRadioGroup
                label="When to use merchant carriers for return labels"
                options={[
                  {
                    id: MerchantCarrierReturnLabelMode.ONLY_SOME_COUNTRIES,
                    label: "Use for specific countries",
                  },
                  {
                    id: MerchantCarrierReturnLabelMode.ALWAYS,
                    label: "Always use merchant carriers",
                  },
                  {
                    id: MerchantCarrierReturnLabelMode.CHEAPEST_OF_REDO_OR_MERCHANT,
                    description:
                      "Uses Redo cost instead of market rates. Merchant label upcharges are not applied.",
                    label: "Use cheapest option",
                  },
                ]}
                setValue={(value) => {
                  merchantCarrierReturnLabels.inputs.mode.setValue(value);
                }}
                value={merchantCarrierReturnLabels.value.mode}
              />

              {merchantCarrierReturnLabels.value.mode ===
                MerchantCarrierReturnLabelMode.ONLY_SOME_COUNTRIES && (
                <Flex dir="column" gap="sm">
                  <div>
                    <RedoTagInputField
                      inputValue={countryInputValue}
                      label="Select countries where merchant carriers will be used"
                      placeholder="Type a country name to search"
                      ref={setCountryAnchorRef}
                      setInputValue={(value) => {
                        setCountryInputValue(value);
                        setDropdownOpen(value.length > 0);
                      }}
                      setTags={(tags) => {
                        // Convert displayed country names back to codes
                        const countryCodes = tags
                          .map((name) => {
                            // Find the country code for this name
                            const entry = Object.entries(
                              RedoShopifyCountryNames,
                            ).find(([_, countryName]) => countryName === name);
                            return entry
                              ? (entry[0] as RedoShopifyCountryCode)
                              : undefined;
                          })
                          .filter(Boolean) as RedoShopifyCountryCode[];

                        merchantCarrierReturnLabels.inputs.whitelistedCountries.setValue(
                          countryCodes,
                        );
                      }}
                      tags={merchantCarrierReturnLabels.value.whitelistedCountries.map(
                        (code: RedoShopifyCountryCode) => ({
                          text:
                            RedoShopifyCountryNames[
                              code as keyof typeof RedoShopifyCountryNames
                            ] || code,
                          color: "gray",
                        }),
                      )}
                    />
                  </div>

                  <RedoListDropdown<string>
                    dropdownAnchor={countryAnchorRef}
                    dropdownOpen={dropdownOpen && countryInputValue.length > 0}
                    fitToAnchor
                    items={Object.entries(RedoShopifyCountryNames)
                      .filter(
                        ([code, name]) =>
                          name
                            .toLowerCase()
                            .includes(countryInputValue.toLowerCase()) &&
                          !merchantCarrierReturnLabels.value.whitelistedCountries.includes(
                            code as RedoShopifyCountryCode,
                          ),
                      )
                      .map(([code, name]) => ({
                        id: code,
                        type: "text" as const,
                        text: name,
                        value: code,
                      }))}
                    itemSelected={({ item }) => {
                      const selectedCode = item.value as RedoShopifyCountryCode;
                      const updatedCodes = [
                        ...merchantCarrierReturnLabels.value
                          .whitelistedCountries,
                        selectedCode,
                      ];
                      merchantCarrierReturnLabels.inputs.whitelistedCountries.setValue(
                        updatedCodes,
                      );
                      setCountryInputValue("");
                      setDropdownOpen(false);
                    }}
                    preventCloseOnAnchorClick
                    setDropdownOpen={setDropdownOpen}
                  />
                </Flex>
              )}
            </>
          )}
        </Flex>

        <Flex justify="space-between" w="full">
          {rateTableDataInput.changed && (
            <RedoButton
              hierarchy="primary"
              onClick={save}
              pending={savePending}
              size="lg"
              text="Save table"
              theme="normal"
            />
          )}
          <RedoButton
            hierarchy="tertiary"
            IconLeading={CopyIcon}
            onClick={copyTable}
            size="lg"
            text="Copy table"
            theme="normal"
          />
          <RedoButton
            hierarchy="tertiary"
            IconLeading={PlusIcon}
            onClick={addRightColumn}
            size="lg"
            text="Add column"
            theme="normal"
          />
          <RedoButton
            hierarchy="tertiary"
            IconLeading={PlusIcon}
            onClick={addBottomRow}
            size="lg"
            text="Add row"
            theme="normal"
          />
        </Flex>
        <Flex dir="column">
          <Flex dir="row">
            <LabelRateTableForm
              deleteColumn={deleteColumn}
              deleteRow={deleteRow}
              input={rateTableDataInput}
              xAxisLabel="Zone"
              yAxisLabel="Max LB"
            />
          </Flex>
          <Flex dir="column" pt="md" w="xs">
            <FormTextInput
              input={rateTableDataInput.inputs.defaultValue}
              label="Default value (used if no match is found in the table)"
              min={0}
              type="number"
            />
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
});
